import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { HTTPError } from '~/shared/http-fetcher/http-error';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconArchive, IconDelete, IconPeopleAdd, IconPlus } from '~/ui/core/icons';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useConfirm } from '../../job-architecture-v2';
import { useUrlTiles } from '../../url-tiles';
import {
    useCompleteLearningOpportunity,
    useDeleteLearningOpportunityMutation,
    useInvalidateLearningOpportunityRelatedCaches,
    useLearningOpportunityPermissions,
    useUpdateLearningOpportunityMutation,
} from '../api';
import { LEARNING_OPPORTUNITY_TILE_SLOT_KEY } from '../constants';
import type { LearningOpportunity } from '../data-contract';
import { buildLearningOpportunityStateUpdate } from '../helpers';
import { FeatureLearningOpportunityDialog } from './FeatureLearningOpportunityDialog';
import { TakeCourseButton } from './TakeCourseButton';

const CONFLICT_STATUS = 409;

const isAlreadyCompletedError = (error: unknown): boolean =>
    error instanceof HTTPError && error.response.status === CONFLICT_STATUS;

export const LearningOpportunityActions: FunctionComponent<{
    readonly canManageFeaturedLearningOpportunities: boolean;
    readonly learningOpportunity: LearningOpportunity;
}> = ({ canManageFeaturedLearningOpportunities, learningOpportunity }) => {
    const translate = useTranslate();
    const { closeTile } = useUrlTiles();
    const { showErrorMessageSnackbar, showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const { canUserDeleteLearningOpportunity, canUserEditLearningOpportunity } = useLearningOpportunityPermissions(
        learningOpportunity.id,
    );
    const { mutate: deleteOpportunity } = useDeleteLearningOpportunityMutation();
    const {
        mutate: completeLearningOpportunity,
        isPending: isCompleting,
        isSuccess: isCompleted,
        error: completionError,
    } = useCompleteLearningOpportunity(learningOpportunity.id);
    const isAlreadyCompleted = isCompleted || isAlreadyCompletedError(completionError);
    const updateMutation = useUpdateLearningOpportunityMutation('patchJson', learningOpportunity.id);
    const { confirm } = useConfirm();
    const invalidateRelatedCaches = useInvalidateLearningOpportunityRelatedCaches();
    const archive = async (): Promise<void> => {
        if (updateMutation.isPending) return;
        try {
            await confirm({
                description: translate('pages.learning-opportunities.archive-confirmation-description'),
                title: translate('pages.job-architecture-v2.archive-title'),
                type: 'default',
            });
        } catch {
            return;
        }
        updateMutation.mutate(buildLearningOpportunityStateUpdate('archived'), {
            onError: showErrorSnackbar,
            onSuccess: () => {
                showSuccessSnackbar('success.learning-opportunities.edited');
                invalidateRelatedCaches();
            },
        });
    };
    const requestDelete = async (): Promise<void> => {
        try {
            await confirm({
                description: translate('pages.learning-opportunities.delete-confirmation-description'),
                title: translate('pages.learning-opportunities.delete-confirmation-title'),
                type: 'destructive',
            });
        } catch {
            return;
        }
        deleteOpportunity(learningOpportunity.id, {
            onError: showErrorSnackbar,
            onSuccess: () => {
                showSuccessSnackbar('success.learning-opportunities.deleted');
                invalidateRelatedCaches();
                closeTile(LEARNING_OPPORTUNITY_TILE_SLOT_KEY);
            },
        });
    };

    return (
        <div className="flex items-center gap-s">
            <TakeCourseButton learningOpportunity={learningOpportunity} />
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <Button size="sm" variant="secondary">
                        {translate('common.options')}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {canManageFeaturedLearningOpportunities && (
                        <FeatureLearningOpportunityDialog
                            learningOpportunityId={learningOpportunity.id}
                            trigger={
                                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                                    <IconPeopleAdd size={14} />
                                    {translate('dialogs.learning-opportunities.title')}
                                </DropdownMenuItem>
                            }
                        />
                    )}
                    <DropdownMenuItem
                        disabled={isCompleting || isAlreadyCompleted}
                        onSelect={() =>
                            completeLearningOpportunity(undefined, {
                                onError: (error) =>
                                    showErrorMessageSnackbar(
                                        isAlreadyCompletedError(error)
                                            ? 'errors.component.learning-opportunities.already-completed'
                                            : 'errors.component.learning-opportunities.add-to-profile',
                                    ),
                                onSuccess: () => showSuccessSnackbar('success.learning-opportunities.added-to-profile'),
                            })
                        }
                    >
                        <IconPlus size={14} />
                        {translate('components.add-to-profile-button')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        disabled={
                            !canUserEditLearningOpportunity ||
                            learningOpportunity.state === 'archived' ||
                            updateMutation.isPending
                        }
                        onSelect={archive}
                    >
                        <IconArchive size={14} />
                        {translate('pages.job-architecture-v2.archive')}
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={!canUserDeleteLearningOpportunity} onSelect={requestDelete}>
                        <IconDelete size={14} />
                        {translate('common.delete')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};
